import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, WidgetSelectGrid, WidgetSelectGridItem } from '../../../../rapid-ui'
import { commonColorOptions, MetaOverlayConfiguration } from '../../../../views'
import type { SimulatorType } from '../../../tracking'
import { trackOverlayEdited } from '../../../tracking'

function getOverlayPresets(t: (key: string) => string): Array<{
	preset: Partial<MetaOverlayConfiguration>
	option: WidgetSelectGridItem<string>
}> {
	return [
		{
			preset: { fillMode: 'none' },
			option: {
				id: 'none',
				label: t('editor.overlay.none'),
				render: () => <></>,
			},
		},
		{
			preset: {
				fillMode: 'flat',
				fillFlatColor: '#000000',
				maskFlatOpacity: 0.5,
				maskMode: 'flat',
				fillBlendMode: 'normal',
			},
			option: {
				id: 'flat',
				label: t('editor.overlay.flatColor'),
				render: () => <div className="bg-black/5 rounded-lg" />,
			},
		},
		{
			preset: {
				fillMode: 'flat',
				fillFlatColor: '#000000',
				maskMode: 'gradient',
				maskGradientDirection: 'to-r',
				maskGradientStop1: 0,
				maskGradientStop2: 1,
				maskGradientOpacity1: 0.5,
				maskGradientOpacity2: 0,
				fillBlendMode: 'normal',
			},
			option: {
				id: 'grad-right',
				label: t('editor.overlay.gradientRight'),
				render: () => <div className="bg-gradient-to-r from-black/0 to-black/10 rounded-lg" />,
			},
		},
		{
			preset: {
				fillMode: 'flat',
				fillFlatColor: '#000000',
				maskMode: 'gradient',
				maskGradientDirection: 'to-t',
				maskGradientStop1: 0,
				maskGradientStop2: 1,
				maskGradientOpacity1: 0.5,
				maskGradientOpacity2: 0,
				fillBlendMode: 'normal',
			},
			option: {
				id: 'grad-bottom',
				label: t('editor.overlay.gradientBottom'),
				render: () => <div className="bg-gradient-to-b from-black/0 to-black/10 rounded-lg" />,
			},
		},
	]
}

export function SimpleOverlayEditor({ overlay, onChange, simulatorType }: {
	overlay: MetaOverlayConfiguration
	onChange: (overlay: MetaOverlayConfiguration) => void
	simulatorType: SimulatorType
}) {
	const { t } = useTranslation('common')
	const overlayPresets = getOverlayPresets(t)
	const matchesPreset = (preset: Partial<MetaOverlayConfiguration>, current: MetaOverlayConfiguration) => {
		return Object.entries(preset).every(([key, value]) => (current as any)[key] === value)
	}

	const selectedPresetId = overlayPresets.find(({ preset }) => matchesPreset(preset, overlay))?.option.id ?? null

	const cycleColors = useCallback(() => {
		// if the current color is in the common color options, cycle to the next one
		const currentColor = overlay.fillFlatColor
		const currentIndex = commonColorOptions.indexOf(currentColor)

		let nextIndex = (currentIndex + 1) % commonColorOptions.length
		let nextColor = commonColorOptions[nextIndex]

		onChange({ ...overlay, fillFlatColor: nextColor })
	}, [overlay, onChange])

	return (
		<div className="grid grid-cols-[1fr_auto] items-center gap-2">
			<div>
				<WidgetSelectGrid
					value={selectedPresetId}
					onChange={(value) => {
						trackOverlayEdited('preset', simulatorType)
						onChange({ ...overlay, ...overlayPresets.find(({ option }) => option.id === value)?.preset })
					}}
					items={overlayPresets.map(({ option }) => option)}
				/>
			</div>
			<Button
				disabled={overlay.fillMode === 'none'}
				type="subtle"
				size="short"
				prefix="refresh"
				onClick={() => {
					trackOverlayEdited('cycle_colors', simulatorType)
					cycleColors()
				}}
			>
				{t('editor.overlay.cycleColors')}
			</Button>
		</div>
	)
}
